import { SettingsDto } from "./settings.dto";

/** Ant Design form values: selects use "0" | "1"; extras is JSON text. */
export type SettingsFormValues = Omit<
  SettingsDto,
  | "forceUpdateApp"
  | "freeDraftTokensEnabled"
  | "inAppPurchaseEnabled"
  | "maintenanceModeEnabled"
  | "offseasonAutoStartEnabled"
  | "extras"
> & {
  forceUpdateApp: "0" | "1";
  freeDraftTokensEnabled: "0" | "1";
  inAppPurchaseEnabled: "0" | "1";
  maintenanceModeEnabled: "0" | "1";
  offseasonAutoStartEnabled: "0" | "1";
  extras: string;
};

export function settingsFormValuesToDto(values: SettingsFormValues): SettingsDto {
  const raw = values.extras.trim() === "" ? "{}" : values.extras;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("INVALID_EXTRAS_JSON");
  }
  const isPlainObject =
    typeof parsed === "object" &&
    parsed !== null &&
    !Array.isArray(parsed);
  if (!isPlainObject) {
    throw new Error("INVALID_EXTRAS_JSON");
  }
  const extras = parsed as SettingsDto["extras"];

  const selectToBool = (v: "0" | "1"): boolean => v === "1";

  const rankedRoyaleDraftsPerDayNR = Number(values.rankedRoyaleDraftsPerDayNR);
  const draftsToUnlockRankedRoyaleMode = Number(
    values.draftsToUnlockRankedRoyaleMode,
  );
  const maxFinishPositionToUnlockRankedMode = Number(
    values.maxFinishPositionToUnlockRankedMode,
  );

  if (
    !Number.isInteger(rankedRoyaleDraftsPerDayNR) ||
    !Number.isInteger(draftsToUnlockRankedRoyaleMode) ||
    !Number.isInteger(maxFinishPositionToUnlockRankedMode)
  ) {
    throw new Error("INVALID_RANKED_ROYALE_INTEGER");
  }

  return {
    usableDraftTokensPerDay: Number(values.usableDraftTokensPerDay),
    draftsPerReferral: Number(values.draftsPerReferral),
    draftTokensPerDay: Number(values.draftTokensPerDay),
    viewSolutionLimitPerDay: Number(values.viewSolutionLimitPerDay),
    initialFreeSolutionLimit: Number(values.initialFreeSolutionLimit),
    sessionReplayCount: Number(values.sessionReplayCount),
    minScoreToUnlockRankedMode: Math.max(
      0,
      Number(values.minScoreToUnlockRankedMode),
    ),
    draftsToUnlockRankedMode: Math.max(
      0,
      Number(values.draftsToUnlockRankedMode),
    ),
    maxFinishPositionToUnlockRankedMode: Math.max(
      1,
      maxFinishPositionToUnlockRankedMode,
    ),
    rankedRoyaleDraftsPerDayNR: Math.max(1, rankedRoyaleDraftsPerDayNR),
    draftsToUnlockRankedRoyaleMode: Math.max(1, draftsToUnlockRankedRoyaleMode),
    freeDraftTokensEnabled: selectToBool(values.freeDraftTokensEnabled),
    forceUpdateApp: selectToBool(values.forceUpdateApp),
    inAppPurchaseEnabled: selectToBool(values.inAppPurchaseEnabled),
    maintenanceModeEnabled: selectToBool(values.maintenanceModeEnabled),
    offseasonAutoStartEnabled: selectToBool(values.offseasonAutoStartEnabled),
    extras: extras ?? {},
  };
}
